import { devWarn, devError, devInfo } from "@/lib/logger";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMe, api } from "@/lib/api";
import { startGoogleLogin } from "@/lib/mobileAuth";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { Countdown } from "@/components/Countdown";
import { Leaderboard } from "@/components/Leaderboard";
import { ReferralBoost } from "@/components/ReferralBoost";
import { SkipTheLine } from "@/components/SkipTheLine";
import { TipJar } from "@/components/TipJar";
import { SalesTicker } from "@/components/SalesTicker";

const WaitlistWidget = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | ok | dup | err
  const [msg, setMsg] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [referralCode, setReferralCode] = useState("");

  // Capture ?ref=... from URL so referred visitors credit their inviter on signup.
  // Also capture ?utm_source=... so we can measure which channel converts best.
  const urlParams = new URLSearchParams(window.location.search);
  const referredBy = urlParams.get("ref");
  const utmSource = urlParams.get("utm_source") || urlParams.get("source");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("busy");
    setMsg("");
    try {
      const r = await api.post("/waitlist", {
        email: email.trim(),
        source: utmSource || "landing",
        referred_by: referredBy || undefined,
      });
      // Prefer the short /api/r/CODE URL for user-facing share links (cleaner in SMS/copy).
      setShareUrl(r?.data?.short_url || r?.data?.share_url || "");
      setReferralCode(r?.data?.referral_code || "");
      if (r?.data?.already_subscribed) {
        setState("dup");
        setMsg("You're already on the list — here's your referral link to bring friends and jump the queue:");
      } else {
        setState("ok");
        setMsg("You're in! Bring friends with your referral link — the more you invite, the earlier you get access:");
      }
    } catch (err) {
      setState("err");
      setMsg(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  const isSuccess = state === "ok" || state === "dup";

  return (
    <section
      data-testid="waitlist-widget"
      className="border-t border-zinc-800 mt-12"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
        <div className="inline-block border border-[#a855f7]/40 bg-[#a855f7]/10 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono mb-6">
          // Free tier · payments live
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-6">
          JOIN THE <span className="neon-green">WAITLIST</span> — FREE FOREVER TIER.
        </h2>
        {/* Live countdown */}
        <div className="mb-8" data-testid="waitlist-countdown-wrap">
          <Countdown testId="waitlist-countdown" />
        </div>
        <p className="text-sm text-zinc-200 max-w-xl mx-auto mb-8">
          Payments are LIVE. Drop your email to lock in the free tier, or skip the line above
          for $9 lifetime VIP. The more friends you refer, the faster your access unlocks.
        </p>
        {isSuccess ? (
          <div data-testid="waitlist-success" className="border border-[#22c55e]/40 bg-[#22c55e]/10 text-[#22c55e] font-mono text-sm px-4 py-5 max-w-xl mx-auto">
            <div>✓ {msg}</div>
            {shareUrl && referralCode && (
              <ReferralBoost referralCode={referralCode} shareUrl={shareUrl} />
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row items-center gap-3 max-w-xl mx-auto">
            <input
              data-testid="waitlist-email-input"
              type="email"
              autoComplete="email"
              placeholder="you@anywhere.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={state === "busy"}
              className="w-full bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-4 py-3 text-white font-mono text-sm placeholder:text-zinc-500 disabled:opacity-50"
            />
            <button
              data-testid="waitlist-submit-btn"
              type="submit"
              disabled={state === "busy" || !email}
              className="w-full sm:w-auto shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#a855f7] hover:bg-[#c084fc] px-6 py-3 font-black disabled:opacity-50 transition-colors"
            >
              {state === "busy" ? "Sending…" : "Notify me →"}
            </button>
          </form>
        )}
        {state === "err" && (
          <div data-testid="waitlist-error" className="mt-3 text-xs text-red-400 font-mono max-w-xl mx-auto">
            {msg}
          </div>
        )}
        {referredBy && !isSuccess && (
          <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono">
            // Referred by {referredBy} — you&apos;ll be credited
          </div>
        )}
        <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-mono">
          // No spam · unsubscribe any time
        </div>
      </div>
    </section>
  );
};

const TickerStrip = () => {
  const items = [
    "FACELESS YT SHORTS +220% YoY",
    "AI PROMPTS · $20–300/WK",
    "NOTION TEMPLATES · PASSIVE",
    "POSTURE CORRECTOR · 75% MARGIN",
    "USERTESTING · $10/20MIN",
    "COLD EMAIL AGENCY · $1500/MO",
    "GALAXY PROJECTOR · TT VIRAL",
    "AI HEADSHOTS · $35/PACK",
  ];
  const loop = [...items, ...items];
  return (
    <div className="overflow-hidden border-y border-zinc-800 bg-black/60 py-3" data-testid="ticker-strip">
      <div className="animate-ticker whitespace-nowrap flex gap-10 text-xs font-mono tracking-widest">
        {loop.map((t, i) => (
          <span key={`${t}-${i}`} className="text-[#60a5fa]">◆ {t}</span>
        ))}
      </div>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // skip auto-check if returning from oauth
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        await fetchMe();
        navigate("/dashboard", { replace: true });
      } catch (err) {
        devWarn("Landing auto-redirect skipped:", err?.message || err);
        setChecking(false);
      }
    })();
  }, [navigate]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // startGoogleLogin() automatically detects Capacitor native shells and routes via
    // the system browser (SFSafariViewController / Chrome Custom Tabs) to avoid Google's
    // WebView block. On the web it does a normal top-level redirect.
    return startGoogleLogin();
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-zinc-300 text-xs tracking-[0.3em] uppercase font-mono">
        Booting
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white relative overflow-hidden">
      {/* Top bar */}
      <header className="border-b border-zinc-800/60 bg-black/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="#operated-by" data-testid="header-brand-nc-link" className="flex items-center gap-2 group" aria-label="NC Construction LLC">
              <div className="h-10 w-24 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden group-hover:border-[#60a5fa]/60 transition-colors">
                <img
                  data-testid="brand-logo-nc"
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/0086jwtl_NC%20Const.Logo%20Editable%20Jul%2017%2C%202026%2C%2012_37_50%20AM.png"
                  alt="NC Construction LLC"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </a>
            <a href="#operated-by" data-testid="header-brand-clark-link" className="flex items-center gap-2 group" aria-label="Clark Properties">
              <div className="h-10 w-24 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden group-hover:border-[#e2e8f0]/60 transition-colors">
                <img
                  data-testid="brand-logo-clark"
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/c2mpfljt_image.png"
                  alt="Clark Properties"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </a>
            <a href="#operated-by" data-testid="header-brand-vip-link" className="flex items-center gap-2 group" aria-label="MoneyHub VIP">
              <div className="h-10 w-24 shrink-0 bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-1 overflow-hidden group-hover:border-[#a855f7]/60 transition-colors">
                <img
                  data-testid="brand-logo-vip"
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/7fk0o84l_MoneyHub%20LogoBest%20Image%20Jul%2017%2C%202026%2C%2012_33_13%20AM.png"
                  alt="MoneyHub VIP"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </a>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-heading font-black tracking-tight text-sm">NC CONSTRUCTION × CLARK PROPERTIES × MONEYHUB VIP</span>
              <span className="text-[9px] tracking-[0.3em] uppercase text-[#60a5fa] mt-0.5">Money/Hub · v1.0</span>
            </div>
          </div>
          <a
            data-testid="header-signin-link"
            href="/login"
            className="text-[10px] tracking-[0.3em] uppercase text-white border border-white/60 hover:bg-white hover:text-black transition-colors px-4 py-2.5 font-bold"
          >
            Sign in
          </a>
        </div>
      </header>

      <TickerStrip />

      <main id="main">

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 relative">
        {/* Big wide MoneyHub VIP hero banner */}
        <div className="w-full mb-10 flex justify-center" data-testid="hero-moneyhub-banner">
          <img
            src={require("../assets/moneyhub-vip-hero.png")}
            alt="MoneyHub VIP"
            className="w-full max-w-5xl h-auto object-contain drop-shadow-[0_0_45px_rgba(96,165,250,0.35)]"
          />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[#00F0FF] mb-6 font-mono">// Live · trending {new Date().toLocaleString("en-US", { month: "short", year: "numeric" })}</div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tighter">
              DISCOVER.<br />
              <span className="neon-green">PLAN.</span><br />
              STAY AHEAD.
            </h1>
            <p className="mt-8 text-zinc-200 max-w-xl text-sm md:text-base leading-relaxed">
              5 income streams. 1 dashboard. AI surfaces the highest-signal / lowest-effort ways to earn
              <span className="text-white"> right now</span> — side hustles, gigs, dropship products, crypto movers, and pay-per-task work.
            </p>
            <p className="mt-4 text-zinc-200 max-w-xl text-sm leading-relaxed">
              Stop hopping between Fiverr, Upwork, TikTok Shop, CoinGecko, and UserTesting. MoneyHub scrapes,
              scores, and refreshes every stream every day — then hands you a 7-day AI action plan for every
              opportunity you save. Built for creators, side-hustlers, and small operators who want a single
              place to plan and cash out their next move.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <GoogleSignInButton
                testId="login-google-hero"
                size="lg"
                variant="light"
                label="Continue with Google"
              />
              <a
                href="#streams"
                data-testid="see-streams-cta"
                className="bg-transparent text-[#e2e8f0] font-bold uppercase tracking-widest text-sm px-7 py-4 border border-[#e2e8f0] hover:bg-[#e2e8f0]/10 transition-all rounded-none"
              >
                See the 5 streams
              </a>
            </div>

            {/* Instant $9 Skip-The-Line — public Stripe checkout, no signup needed */}
            <div className="mt-8">
              <SkipTheLine />
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
              {[
                {k: "Avg. payout", v: "$35–$1.5k", c: "text-[#60a5fa]"},
                {k: "Time to first $", v: "Same day", c: "text-[#e2e8f0]"},
                {k: "Streams", v: "5 active", c: "text-white"},
              ].map((m)=>(
                <div key={m.k} className="border border-zinc-800 bg-zinc-950/60 p-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-200">{m.k}</div>
                  <div className={`font-heading text-2xl font-black mt-2 ${m.c}`}>{m.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right side mock terminal */}
          <div className="lg:col-span-5">
            <div className="border border-[#60a5fa]/30 bg-black p-6 glow-border-green relative">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-2 w-2 rounded-full bg-[#fb7185]" />
                <div className="h-2 w-2 rounded-full bg-[#e2e8f0]" />
                <div className="h-2 w-2 rounded-full bg-[#60a5fa]" />
                <span className="ml-2 text-[10px] tracking-[0.3em] uppercase text-zinc-300">money-hub.sh</span>
              </div>
              <pre className="text-[12px] leading-6 text-zinc-300 font-mono">
{`> scanning trending opportunities…
[✓] faceless-shorts        signal 92
[✓] ai-prompts             signal 88
[✓] notion-templates       signal 74
[✓] cold-email-agency      signal 81
[✓] dropship: galaxy-proj  signal 91
[✓] crypto-mover: SOL      +6.2%
> ranking by opportunity / effort…`}
              </pre>
              <div className="mt-4 border-t border-zinc-800 pt-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-2">Top pick right now</div>
                <div className="flex items-baseline justify-between">
                  <div className="font-heading text-xl font-bold">AI Headshots gig</div>
                  <div className="font-mono neon-green">signal 94</div>
                </div>
                <div className="text-xs text-zinc-200 mt-1">Same-day delivery · Fiverr · 30 min/job</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Streams grid */}
      <section id="streams" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="border-t border-zinc-800 pt-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-200 mb-6 font-mono">// 05 income streams</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-3xl">
            Every hustle worth doing. <span className="neon-cyan">In one dashboard.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
            {[
              {n:"01", t:"AI Side-Hustle Generator", d:"Claude-powered ideas tuned to your skills + time. Action plans included.", c:"#60a5fa"},
              {n:"02", t:"Freelance Gigs Board", d:"Hottest Fiverr/Upwork gigs ranked by speed-to-cash.", c:"#00F0FF"},
              {n:"03", t:"Dropship Products", d:"Trending products w/ margin %, source links, virality score.", c:"#60a5fa"},
              {n:"04", t:"Crypto Movers", d:"Live top-8 coins, % moves, sparkline charts.", c:"#fb7185"},
              {n:"05", t:"Micro-Tasks", d:"User testing, surveys, AI training — paid-per-task.", c:"#e2e8f0"},
            ].map((s)=>(
              <div key={s.n} className="border border-zinc-800/70 hover:border-zinc-600 bg-zinc-950/60 p-6 transition-all group">
                <div className="font-mono text-xs tracking-[0.3em] text-zinc-300 group-hover:text-zinc-200 transition-colors">{s.n}</div>
                <div className="font-heading text-lg font-bold mt-4" style={{color: s.c}}>{s.t}</div>
                <p className="mt-3 text-sm text-zinc-200 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works — SEO content block */}
      <section id="how-it-works" aria-label="How Money/Hub works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="border-t border-zinc-800 pt-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-200 mb-6 font-mono">// How it works</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-3xl">
            Fewer tabs. <span className="neon-cyan">More cash.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <article className="border border-zinc-800/70 bg-zinc-950/60 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-3">Step 01 · Discover</div>
              <h3 className="font-heading text-xl font-black mb-3">AI-ranked opportunities, refreshed daily.</h3>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Money/Hub pulls trending freelance gigs from Fiverr and Upwork, top-selling dropship products
                from TikTok Shop and Amazon, live crypto and stock movers from CoinGecko and Alpha Vantage,
                and paid micro-tasks from UserTesting, Prolific, and Respondent — all scored by our AI for
                payout, effort, and time-to-first-dollar, so you can skip the research and start earning.
              </p>
            </article>

            <article className="border border-zinc-800/70 bg-zinc-950/60 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#00F0FF] font-mono mb-3">Step 02 · Plan</div>
              <h3 className="font-heading text-xl font-black mb-3">Save one. Get a 7-day action plan.</h3>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Save any opportunity to your dashboard and hit &quot;Plan&quot; — Claude generates a detailed
                day-by-day playbook: exact keywords, pricing, first-week outreach scripts, portfolio prompts,
                and revenue milestones. Set an email digest so the best new opportunities each morning arrive
                in your inbox before the crowd sees them.
              </p>
            </article>

            <article className="border border-zinc-800/70 bg-zinc-950/60 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] font-mono mb-3">Step 03 · Cash out</div>
              <h3 className="font-heading text-xl font-black mb-3">One-tap deposit to Cash App.</h3>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Add your <span className="text-white font-mono">$cashtag</span> once and every stream shows a
                green &quot;Deposit → Cash App&quot; button. Whether you closed a Fiverr gig, sold a Notion
                template, or exited a crypto position, you go from earned to deposited in a single tap — no
                Stripe delays, no PayPal fees, no manual bank transfers.
              </p>
            </article>
          </div>

          <div className="mt-10 border border-zinc-800/70 bg-zinc-950/40 p-6 max-w-4xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-3 font-mono">// Who it&apos;s for</div>
            <p className="text-sm text-zinc-200 leading-relaxed">
              Side-hustlers stacking $500–$5,000/mo alongside a day job. Creators diversifying beyond ads and
              sponsorships. Freelancers hunting the next trending niche before it saturates. Small business
              operators — like NC Construction LLC and Clark Properties — looking to redirect downtime into
              real weekly income. Money/Hub replaces a browser full of tabs with a single command center for
              five proven online income streams, updated every day and tuned to what pays right now.
            </p>
          </div>
        </div>
      </section>

      </main>

      <WaitlistWidget />
      <Leaderboard limit={10} />

      {/* Tip Jar — low-friction support */}
      <section className="border-t border-zinc-800 mt-4 py-12 px-4">
        <TipJar />
      </section>

      {/* Live sales ticker — social-proof pill (bottom-left, dismissible) */}
      <SalesTicker />

      <footer className="border-t border-zinc-800 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-8 font-mono text-center">// Operated by</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <a href="#" data-testid="footer-brand-nc" className="group border border-zinc-800 hover:border-[#60a5fa]/40 bg-black/40 p-6 flex flex-col items-center gap-4 transition-all hover:drop-shadow-[0_0_25px_rgba(59,130,246,0.15)]">
              <div className="h-20 w-full bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-2 overflow-hidden">
                <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/0086jwtl_NC%20Const.Logo%20Editable%20Jul%2017%2C%202026%2C%2012_37_50%20AM.png" alt="NC Construction LLC" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="w-full bg-zinc-900 border border-zinc-800 rounded-md py-3 px-2 text-center">
                <div className="font-heading text-base font-black leading-tight">NC Construction LLC</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">Operating partner</div>
              </div>
            </a>
            <a href="#" data-testid="footer-brand-clark" className="group border border-zinc-800 hover:border-[#e2e8f0]/40 bg-black/40 p-6 flex flex-col items-center gap-4 transition-all hover:drop-shadow-[0_0_25px_rgba(226,232,240,0.15)]">
              <div className="h-20 w-full bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-2 overflow-hidden">
                <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/c2mpfljt_image.png" alt="Clark Properties" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="w-full bg-zinc-900 border border-zinc-800 rounded-md py-3 px-2 text-center">
                <div className="font-heading text-base font-black leading-tight">Clark Properties</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">Operating partner</div>
              </div>
            </a>
            <a href="#" data-testid="footer-brand-vip" className="group border border-zinc-800 hover:border-[#a855f7]/40 bg-black/40 p-6 flex flex-col items-center gap-4 transition-all hover:drop-shadow-[0_0_25px_rgba(168,85,247,0.15)]">
              <div className="h-20 w-full bg-zinc-900 border border-zinc-800 rounded-md flex items-center justify-center p-2 overflow-hidden">
                <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_quick-cash-mvp-2/artifacts/7fk0o84l_MoneyHub%20LogoBest%20Image%20Jul%2017%2C%202026%2C%2012_33_13%20AM.png" alt="MoneyHub VIP" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="w-full bg-zinc-900 border border-zinc-800 rounded-md py-3 px-2 text-center">
                <div className="font-heading text-base font-black leading-tight">MoneyHub VIP</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 mt-1">Operating partner</div>
              </div>
            </a>
          </div>
          <div className="text-center text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mt-10">
            © 2026 NC Construction LLC × Clark Properties × MoneyHub VIP · Money/Hub
          </div>
          <nav className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-[10px] tracking-[0.3em] uppercase font-mono text-zinc-200" data-testid="landing-legal-nav">
            <a href="/discover" className="hover:text-white transition-colors" data-testid="landing-link-discover">Discover</a>
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="landing-link-privacy">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="landing-link-terms">Terms</a>
            <a href="/dpa" className="hover:text-white transition-colors" data-testid="landing-link-dpa">DPA</a>
            <a href="/refund" className="hover:text-white transition-colors" data-testid="landing-link-refund">Refund</a>
            <a href="/msa" className="hover:text-white transition-colors" data-testid="landing-link-msa">MSA</a>
            <a href="/insurance" className="hover:text-white transition-colors" data-testid="landing-link-insurance">Insurance</a>
            <a href="mailto:moneyhubvip@outlook.com" className="text-[#60a5fa] hover:text-white transition-colors" data-testid="landing-link-support">Support</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
